import { Loader2Icon } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import { Form } from '../ui/form';
import { FieldWrapper } from '../ui/form/field-wrapper';
import { Input } from '../ui/form/input';

import {
  UpdatePatientInput,
  updatePatientInputSchema,
  useUpdatePatient,
} from '@/features/patients/api/edit-patient';
import { usePatient } from '@/features/patients/api/get-patient';
import { GenderSelect } from '@/features/patients/components/patient-gender-select';
import { useModalControls } from '@/state/modals';

export const Config = {
  title: 'Modifier la fiche patient',
  variant: 'dialog' as const,
};
export function Component({ pid }: { pid: string }) {
  const { closeModal } = useModalControls();
  const patientQuery = usePatient({ pid });
  const updatePatientMutation = useUpdatePatient({
    pid,
    mutationConfig: {
      onSuccess: () => {
        toast.success('Fiche patient modifiée');
        closeModal();
      },
      onError: (error) => {
        toast.error('Erreur lors de la modification de la fiche patient');
        console.error(error);
      },
    },
  });

  if (patientQuery.isPending) {
    return <Loader2Icon className="h-6 w-6 animate-spin" />;
  }

  const patient = patientQuery.data;
  if (!patient) return null;

  return (
    <Form
      id="edit-patient"
      schema={updatePatientInputSchema}
      onSubmit={(data) => updatePatientMutation.mutate({ pid, values: data })}
      options={{
        defaultValues: {
          name: patient.name,
          age: patient.age,
          gender: patient.gender,
        } as UpdatePatientInput,
      }}
    >
      {({ register, formState, control }) => (
        <div className="grid gap-2 mb-2">
          <p className="text-sm text-muted-foreground">
            Veuillez remplir les informations du patient
          </p>

          <Input
            name="name"
            type="text"
            registration={register('name')}
            label="Nom"
            error={formState.errors['name']}
          />
          <Input
            name="age"
            type="number"
            min={1}
            max={150}
            registration={register('age')}
            label="Age"
            error={formState.errors['age']}
          />

          <FieldWrapper label="Genre">
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <GenderSelect
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
          </FieldWrapper>

          <div className="flex justify-end">
            <Button
              className="w-full sm:w-1/2 md:w-[initial]"
              form="edit-patient"
              type="submit"
              size="sm"
              isLoading={updatePatientMutation.isPending}
              disabled={updatePatientMutation.isPending}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
}
